import { useState } from 'react';
import { Bug, CirclePlus } from 'lucide-react';
import FileItemDialog from './components/FileItemDialog';
import ItemCard from './components/ItemCard';
import ItemGrid from './components/ItemGrid';
import StatusBadge from './components/StatusBadge';
import ViewItemDialog from './components/ViewItemDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { useZealBugReports, useZealTasks, usePostZealBugReport } from '@/data/zealTask';

// A report Zeal has turned into a task shows that task's id and status; the
// status comes from the task list, so it is blank until that has loaded.
const ReportBadge = ({ report, tasks }) => {
  if (!report.task) {
    return (
      <Badge variant="outline" className="whitespace-nowrap border-red-200 bg-red-50 font-medium text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
        <Bug className="mr-1 h-3 w-3" />
        Not a task yet
      </Badge>
    );
  }
  const status = tasks.data?.content[report.task]?.status;
  return (
    <StatusBadge status={status}>
      <span className="font-mono">{report.task}</span>
      {status && <span className="ml-1">· {status}</span>}
    </StatusBadge>
  );
};

const BugReports = () => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const reports = useZealBugReports();
  const tasks = useZealTasks();
  const postZealBugReport = usePostZealBugReport();

  const items = reports.data ? reports.data.query.map((id) => reports.data.content[id]) : [];

  return (
    <>
      <ItemGrid
        title="Bugs"
        description="Bug reports for the dev team. Zeal turns a report into a task."
        action={
          <Button onClick={() => setIsAddOpen(true)}>
            <CirclePlus />
            Report Bug
          </Button>
        }
        isLoading={reports.isLoading && !reports.data}
        error={reports.error && `An error occurred loading bug reports: ${reports.error.message}`}
        items={items}
        empty={{ icon: <Bug />, title: 'No Bug Reports', description: 'File one with Report Bug' }}
        renderItem={(report) => <ItemCard key={report.id} item={report} badge={<ReportBadge report={report} tasks={tasks} />} onView={() => setViewing(report)} />}
      />

      {isAddOpen && (
        <FileItemDialog
          title="Report Bug"
          description="Say what happened and what you expected. It cannot be edited from here once filed."
          submitLabel="Report Bug"
          onCreate={postZealBugReport}
          getTarget={(report) => `bug:${report.id}`}
          onClose={() => setIsAddOpen(false)}
          onSuccess={() => reports.mutate()}
        />
      )}
      {viewing && <ViewItemDialog item={viewing} badge={<ReportBadge report={viewing} tasks={tasks} />} onClose={() => setViewing(null)} />}
    </>
  );
};

export default BugReports;
